import { Autocomplete, Chip, TextField, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { StaffMember } from '../../utils/validations/clientDetailsForm';
import Avatar from '../Common/Avatar';
import scrollbarStyles from '../../utils/styles';

const CustomTextField = styled(TextField)(({ error, disabled }) => ({
  '& .MuiFilledInput-root': {
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    backgroundColor: disabled ? '#EEE' : '#FFF',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#FFF',
      border: error
        ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
        : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFF',
      boxShadow: 'none',
      border: error
        ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
        : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&.MuiFilledInput-underline:before': {
      borderBottom: 'none !important',
    },
    '&.MuiFilledInput-underline:after': {
      borderBottom: 'none !important',
    },
    padding: '6px 10px',
  },
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    opacity: disabled ? 0.6 : 1,
    backgroundColor: disabled ? 'rgba(204, 204, 204, 0.6)' : 'inherit',
    border: disabled
      ? '1px solid rgba(204, 204, 204, 0.6) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    '&:hover': {
      backgroundColor: disabled ? 'rgba(204, 204, 204, 0.6)' : '#fff',
    },
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '5px',
  backgroundColor: '#7CAEBE26',
  padding: '10px 8px',
  color: theme.palette.primary.main,
  fontFamily: 'Satoshi',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '22px',
  gap: '16px',
  '& .MuiChip-label': {
    padding: 2, // Remove padding from the text
  },
}));

interface ICustomMultiSelectProps {
  options: (object | string | StaffMember)[];
  value?: (object | string)[] | null;
  onChange: (_newValue: (object | string)[]) => void;
  placeholder?: string;
  onKeyDown?: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  onBlur?: (_e: React.FocusEvent<HTMLDivElement, Element>) => void;
  disabled?: boolean;
  onTextFieldChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  freeSolo?: boolean;
  open?: boolean;
  fixedOptions?: StaffMember[];
  withIcon?: boolean;
  loadMoreOptions?: () => void;
}

function CustomMultiSelect({
  options,
  value,
  onChange,
  placeholder,
  onKeyDown,
  label,
  required,
  error,
  onBlur,
  disabled,
  onTextFieldChange,
  inputRef,
  freeSolo,
  open,
  fixedOptions,
  withIcon,
  loadMoreOptions,
}: ICustomMultiSelectProps) {
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" gap="5px">
      <Typography variant="meta" color="primary">
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>
      <Autocomplete
        multiple
        id="tags-autocomplete"
        options={options}
        getOptionLabel={(option) =>
          typeof option === 'object' ? (option as StaffMember).name : (option as string)
        }
        value={value || []}
        open={open}
        freeSolo={freeSolo}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        onBlur={onBlur}
        disabled={disabled}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const ids = fixedOptions?.map((v) => v.id);
            return (
              <StyledChip
                key={key}
                variant="outlined"
                label={typeof option === 'object' ? (option as StaffMember).name : option}
                {...tagProps}
                disabled={
                  disabled ||
                  (typeof option === 'object' && ids?.includes((option as StaffMember).id))
                }
                icon={
                  withIcon ? (
                    <Avatar
                      firstName={(option as StaffMember).name}
                      profilePic={(option as StaffMember).profilePic}
                    />
                  ) : undefined
                }
                deleteIcon={
                  <img
                    src="/assets/icons/icon-park-outline_close.svg"
                    alt="icon-park-outline-close"
                  />
                }
              />
            );
          })
        }
        renderInput={(params) => (
          <CustomTextField
            {...params}
            variant="filled"
            InputLabelProps={{ shrink: false }}
            placeholder={disabled ? '' : placeholder}
            error={error}
            onChange={onTextFieldChange}
            inputRef={inputRef}
          />
        )}
        ListboxProps={{
          sx: {
            maxHeight: '150px',
            overflow: 'auto',
            ...scrollbarStyles,
          },
          onScroll: (event) => {
            const listboxNode = event.currentTarget;

            if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
              if (loadMoreOptions) {
                loadMoreOptions(); // Correctly invoke the function here
              }
            }
          },
        }}
        onKeyDown={onKeyDown}
        componentsProps={{
          clearIndicator: { sx: { display: 'none' } },
          popupIndicator: { sx: { display: 'none' } },
        }}
      />
    </Box>
  );
}

CustomMultiSelect.defaultProps = {
  placeholder: '',
};

export default CustomMultiSelect;
